import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import PropertyAttributeFormModal from 'features/property-attributes/components/PropertyAttributeFormModal';
import DeletePropertyAttributeModal from 'features/property-attributes/components/DeletePropertyAttributeModal';
import { usePropertyAttributes } from 'features/property-attributes/hooks/usePropertyAttributes';
import { useUpdatePropertyAttribute } from 'features/property-attributes/hooks/useUpdatePropertyAttribute';
import { Edit, Layers, Plus, Search, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';

const TABS = [
  { value: 'amenity', label: 'Amenities' },
  { value: 'additionalRoom', label: 'Additional Rooms' },
  { value: 'overlooking', label: 'Overlooking' },
  { value: 'propertyType', label: 'Property Types' },
];

const PropertyAttributes = () => {
  const [activeType, setActiveType] = useState('amenity');
  const [searchInput, setSearchInput] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);

  const { data, isPending, isError, error } = usePropertyAttributes({ type: activeType });
  const { mutate: updateAttribute } = useUpdatePropertyAttribute();

  const attributes = useMemo(() => {
    const list = data?.data || [];
    if (!searchInput.trim()) return list;
    const q = searchInput.trim().toLowerCase();
    return list.filter(
      (a) => a.label?.toLowerCase().includes(q) || a.key?.toLowerCase().includes(q),
    );
  }, [data, searchInput]);

  const handleAddClick = () => {
    setSelectedAttribute(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (attribute) => {
    setSelectedAttribute(attribute);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (attribute) => {
    setSelectedAttribute(attribute);
    setIsDeleteOpen(true);
  };

  const handleToggleActive = (attribute) => {
    updateAttribute({ id: attribute._id, data: { isActive: !attribute.isActive } });
  };

  return (
    <Layout>
      <div className={`min-h-screen bg-background text-foreground p-6 ${PAGE_CONTENT_WIDTH}`}>
        <PageHeader
          icon={Layers}
          title="Property Attributes"
          subtitle="Manage the Amenities, Additional Rooms, Overlooking and Property Type options shown in the provider and customer apps"
          actions={
            <button
              onClick={handleAddClick}
              className="flex items-center space-x-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              <Plus size={16} />
              <span>Add Attribute</span>
            </button>
          }
        />

        {/* Type tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveType(tab.value)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeType === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            type="text"
            placeholder="Search by label or key..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-10 py-2 text-sm"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden elevation-1">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Icon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {isPending ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      Loading attributes...
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-destructive">
                      Error loading attributes: {error?.message || 'Something went wrong'}
                    </td>
                  </tr>
                ) : attributes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No {TABS.find((t) => t.value === activeType)?.label.toLowerCase()} yet — click "Add Attribute" to
                      create one.
                    </td>
                  </tr>
                ) : (
                  attributes.map((attribute) => (
                    <tr key={attribute._id} className="hover:bg-accent/50 transition-colors">
                      <td className="px-4 py-3">
                        {attribute.icon ? (
                          <img
                            src={attribute.icon}
                            alt={attribute.label}
                            className="w-10 h-10 rounded-lg object-cover border border-border bg-muted"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                            <span className="text-xs text-muted-foreground">—</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground whitespace-nowrap">
                        {attribute.label}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{attribute.key}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{attribute.displayOrder ?? 0}</td>
                      <td className="px-4 py-3 text-sm">
                        <label className="relative inline-flex items-center cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={attribute.isActive}
                            onChange={() => handleToggleActive(attribute)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600" />
                          <span className="ms-3 text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                            {attribute.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            className="p-1 hover:bg-muted rounded transition-colors"
                            title="Edit"
                            onClick={() => handleEditClick(attribute)}
                          >
                            <Edit size={16} className="text-muted-foreground hover:text-foreground" />
                          </button>
                          <button
                            className="p-1 hover:bg-muted rounded transition-colors"
                            title="Delete"
                            onClick={() => handleDeleteClick(attribute)}
                          >
                            <Trash2 size={16} className="text-muted-foreground hover:text-destructive" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <PropertyAttributeFormModal
          isOpen={isFormOpen}
          setIsOpen={setIsFormOpen}
          attribute={selectedAttribute}
          defaultType={activeType}
        />
        <DeletePropertyAttributeModal
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          attribute={selectedAttribute}
        />
      </div>
    </Layout>
  );
};

export default PropertyAttributes;
